// What the billing page says about access and the next charge.
//
// Pure on purpose: the interesting cases here are a failing card and a grace
// period, which are tedious to reach through the UI and trivial to assert on.

import { formatPrice, formatShortDate } from "../../i18n/format.js";

const DAY = 1000 * 60 * 60 * 24;

// "2026-10-15" through new Date() is parsed as UTC midnight, which lands on the
// previous day in any negative offset and shifts every day count. The API sends
// calendar days, so they are read as calendar days.
export function parseApiDate(value) {
	if (!value) return null;

	const [year, month, day] = String(value).split("-").map(Number);
	if (!year || !month || !day) return null;

	return new Date(year, month - 1, day);
}

function startOfDay(date) {
	return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function deriveAccessWindow(subscription, today = new Date()) {
	const start = parseApiDate(subscription?.start_date);
	const end = parseApiDate(subscription?.end_date);
	const grace = parseApiDate(subscription?.grace_until);

	// Access runs to whichever is later, because that is what the API means by
	// is_active. Reading end_date alone showed an active plan with a negative
	// number of days left.
	const endsOn = grace && end && grace > end ? grace : end;
	if (!endsOn) return { endsOn: null, daysLeft: 0, progressPercent: 100, inGrace: false };

	const now = startOfDay(today);

	// Inclusive, matching the API: a term is still active on its end_date, so
	// that day counts as one remaining. Rounded rather than ceilinged because a
	// DST change makes the raw difference 15.96 or 16.04 days.
	const daysLeft = Math.max(0, Math.round((endsOn - now) / DAY) + 1);

	const totalDays = start ? Math.max(1, Math.round((endsOn - start) / DAY) + 1) : 0;
	const progressPercent = totalDays
		? Math.min(100, Math.max(0, Math.round(((totalDays - daysLeft) / totalDays) * 100)))
		: 100;

	return {
		endsOn,
		daysLeft,
		progressPercent,
		inGrace: Boolean(grace && end && now > end && now <= grace),
	};
}

export function deriveRenewalNotice(subscription, { t, language }) {
	const status = subscription?.renewal_status;
	if (!status || status === "none") return null;

	const amount = formatPrice(
		subscription.next_renewal_amount,
		subscription.next_renewal_currency,
		language
	);
	const day = (value) => formatShortDate(parseApiDate(value), language);
	const keepsAccessUntil = day(subscription.grace_until);
	const accessUntil = keepsAccessUntil
		? t("payments:renewal.accessUntil", { date: keepsAccessUntil })
		: null;
	const updateCard = { label: t("payments:renewal.updateCard"), kind: "update-card" };

	if (status === "scheduled") {
		const on = day(subscription.next_renewal_date);
		return {
			tone: "info",
			title: t("payments:renewal.scheduled.title"),
			body:
				amount && on
					? t("payments:renewal.scheduled.body", { amount, date: on })
					: t("payments:renewal.scheduled.bodyWithoutDetails"),
			action: null,
		};
	}

	if (status === "retrying") {
		return {
			tone: "warning",
			title: t("payments:renewal.retrying.title"),
			body: [
				amount
					? t("payments:renewal.retrying.failedAmount", { amount })
					: t("payments:renewal.retrying.failed"),
				t("payments:renewal.retrying.retry"),
				accessUntil,
			]
				.filter(Boolean)
				.join(" "),
			action: updateCard,
		};
	}

	// requires_action: an expired card or a bank asking for confirmation.
	// Retrying off-session cannot clear either, so only the user can.
	return {
		tone: "danger",
		title: t("payments:renewal.requiresAction.title"),
		body: [t("payments:renewal.requiresAction.body"), accessUntil]
			.filter(Boolean)
			.join(" "),
		action: updateCard,
	};
}
